<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <permission-button color="primary" class="ml-0 mr-0" @click="open" v-if="!editingBySharedWithMe"
                         :permission="!limitReached"
                         :no-permission-tool-tip="trans('app.sharing.save_limit', {limit: 10})"
      >
        {{ 'app.sharing.add_sharing' | trans }}
      </permission-button>
    </template>
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>
          {{
            (!!sharingId ? 'app.sharing.edit_sharing' : 'app.sharing.add_sharing') | trans
          }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout wrap align-center>
              <v-flex xs12>

                <v-text-field
                    :label="'app.email' | trans"
                    :disabled="!!sharingId"
                    clearable
                    v-model="sharing.email"
                    :rules="rules.email"
                    full
                    v-if="!editingBySharedWithMe"
                ></v-text-field>

              </v-flex>
            </v-layout>
            <h4 class="mt-3">{{ `app.sharing.resources_to_share` | trans }}</h4>
            <p v-if="!editingBySharedWithMe">{{ `app.sharing.decide_shared_by_me` | trans }}</p>
            <p v-else v-html="trans(`app.sharing.decide_shared_with_me`, {name: sharing.shared_by_email})"></p>
            <v-layout wrap>
              <v-flex xs4 v-for="resource in sharing.resources" v-bind:key="resource.type">
                <v-checkbox
                    v-if="!editingBySharedWithMe"
                    :color="`success`"
                    :label="trans('app.sharing.types.' + resource.type)"
                    v-model="resource.shared"
                    :readonly="isProductsCheckboxReadOnly(resource, 'shared')"
                    @change="resourceCheckBoxChanged(resource, 'shared')"
                >
                </v-checkbox>
                <permission-tooltip v-else
                                    :permission="resource.shared"
                                    :tooltip="{top: true, bottom: false, attach: false}"
                                    :no-permission-tool-tip="trans('app.sharing.resource_is_not_shared')"
                >
                  <v-checkbox
                      :color="`success`"
                      :class="{shared_checkbox: resource.shared}"
                      :label="trans('app.sharing.types.' + resource.type)"
                      v-model="resource.enabled"
                      :disabled="!resource.shared"
                      :readonly="isProductsCheckboxReadOnly(resource, 'enabled')"
                      @change="resourceCheckBoxChanged(resource, 'enabled')"
                  >
                  </v-checkbox>
                </permission-tooltip>
              </v-flex>
              <v-flex xs12>
                <v-messages class="ml-2" v-if="noResourceChecked"
                            :value="[trans('app.sharing.no_resource_checked')]"
                            :color="`error`"></v-messages>
              </v-flex>
            </v-layout>
            <v-alert v-if="!!sharingId && !editingBySharedWithMe" type="warning" :value="true" :dismissible="false">
              {{ `app.sharing.warning_shared_by_me` | trans }}
            </v-alert>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save" :disabled="!valid"
               :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {trans} from "../../../app";
import {helpers} from "../../../helpers";
import RulesBuilder from "../../../utils/rulesBuilder";
import {mapGetters, mapActions} from "vuex";

export default {
  data: function () {
    return {
      rulesBuilder: null,
      noResourceChecked: false,
      dialog: false,
      valid: false,
      loading: false,
      sharingId: null,
      sharing: {
        email: '',
        resources: [
          {
            type: 'products',
            shared: false,
            enabled: false,
          },
          {
            type: 'foods',
            shared: false,
            enabled: false,
          },
          {
            type: 'menu_schemas',
            shared: false,
            enabled: false,
          },
          {
            type: 'prescriptions',
            shared: false,
            enabled: false,
          },
          {
            type: 'surveys',
            shared: false,
            enabled: false,
          }
        ],
        shared_by_email: '',
      },
    }
  },
  props: {
    currentSharing: Object,
    editingBySharedWithMe: {
      type: Boolean,
      default: false
    },
  },
  created() {
    this.rulesBuilder = new RulesBuilder();
  },
  mounted() {
    this.fetchSharedByMeCount();
  },
  watch: {
    currentSharing: function (sharing) {
      if (sharing === null) {
        this.sharingId = null;
        this.sharing.email = '';
        this.sharing.resources = [
          {
            type: 'products',
            shared: false,
            enabled: false,
          },
          {
            type: 'foods',
            shared: false,
            enabled: false,
          },
          {
            type: 'menu_schemas',
            shared: false,
            enabled: false,
          },
          {
            type: 'prescriptions',
            shared: false,
            enabled: false,
          },
          {
            type: 'surveys',
            shared: false,
            enabled: false,
          },
        ];
      } else {
        this.sharingId = sharing.id;
        this.sharing.email = sharing.email;
        this.sharing.resources = helpers.deepClone(sharing.resources);
        this.sharing.shared_by_email = sharing.shared_by_email;
      }
    }
  },
  computed: {
    ...mapGetters(['sharedByMeCount']),
    rules: function () {
      return {
        email: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.email'))
              .addIsEmailRule(trans('app.email'))
              .getRules(),
        ]
      };
    },
    limitReached: function () {
      return this.sharedByMeCount >= 10;
    }
  },
  methods: {
    ...mapActions(['fetchSharedByMeCount']),
    open: function () {
      this.$refs.form.reset();
      this.noResourceChecked = false;
      this.sharingId = null;
      this.dialog = true;
    },
    close: function () {
      this.dialog = false;
      this.$refs.form.reset();
      this.noResourceChecked = false;
      this.$emit('sharing_closed');
    },
    save: async function () {
      this.noResourceChecked = false;
      if (this.$refs.form.validate() === false) {
        return;
      }
      let atLeastOneChecked = false;
      for (let resource of this.sharing.resources) {
        if (this.editingBySharedWithMe) {
          if (resource.enabled) {
            atLeastOneChecked = true;
            break;
          }
        } else {
          if (resource.shared) {
            atLeastOneChecked = true;
            break;
          }
        }
      }

      if (!atLeastOneChecked) {
        this.valid = false;
        this.noResourceChecked = true;
        return;
      }

      this.loading = true;
      if (this.sharingId === null) {
        try {
          await axios.post('/v2/sharings', this.sharing);
          this.$toast.success(trans('app.actions.saved', {name: trans('app.sharing.sharing_accusative')}));
        } catch (e) {
          this.$toast.error(trans('app.save_failed'));
        }
      } else {
        const sharingId = this.sharingId;
        try {
          await axios.patch(`/v2/sharings/${sharingId}`, this.sharing);
          this.$toast.success(trans('app.actions.edited', {name: trans('app.sharing.sharing_accusative')}));
        } catch (e) {
          this.$toast.error(trans('app.save_failed'));
        }
      }
      this.$emit('sharing_stored');
      this.fetchSharedByMeCount();
      this.close();
      this.loading = false;
    },
    resourceCheckBoxChanged: function (resource, property) {
      if (resource.type === 'foods' && (property === 'shared' ? resource.shared : resource.enabled)) {

        const productsResource = this.sharing.resources.find((resource) => {
          return resource.type === 'products'
        });

        if (property === 'shared') {
          productsResource.shared = true;
        }
        if (property === 'enabled') {
          productsResource.enabled = true;
        }
      }
      this.noResourceChecked = false;
      this.$refs.form.resetValidation();
    },
    isProductsCheckboxReadOnly: function (productResource, property) {
      if (productResource.type !== 'products') {
        return false;
      }

      const foodsResource = this.sharing.resources.find((resource) => {
        return resource.type === 'foods'
      });

      if (property === 'shared') {
        return !!foodsResource.shared;
      }
      if (property === 'enabled') {
        return !!foodsResource.enabled;
      }

      return false;
    }
  }
}
</script>
<style lang="scss" type="text/scss">
.shared_checkbox .v-label {
  color: black;
}
</style>